import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AdminUser, AdminUserUpdate } from '../../generated/api/types.gen.js';
import { zAdminUserUpdate } from '../../generated/api/zod.gen.js';
import {
  getAdminUserQueryKey,
  listAdminUsersQueryKey,
  updateAdminUserMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import {
  Form,
  SelectFormField,
  SubmitButton,
  SwitchFormField,
  TextAreaFormField,
  TextFormField,
  useZodForm,
} from '../../components/form/index.js';
import { Card, useToast } from '../../components/ui/index.js';
import { Can } from '../../components/auth/Can.js';
import { USER_STATUS_LABEL } from './user-labels.js';

interface UserProfileTabProps {
  user: AdminUser;
}

const STATUS_OPTIONS = (Object.keys(USER_STATUS_LABEL) as AdminUser['status'][]).map((value) => ({
  value,
  label: USER_STATUS_LABEL[value],
}));

/** Edit a person's profile and account state. Remount with a new `key` when the saved user changes. */
export const UserProfileTab: React.FC<UserProfileTabProps> = ({ user }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const update = useMutation(updateAdminUserMutation());

  const form = useZodForm(zAdminUserUpdate, {
    defaultValues: {
      fullName: user.fullName ?? '',
      username: user.username ?? '',
      phone: user.phone ?? '',
      bio: user.bio ?? '',
      jobTitle: user.jobTitle ?? '',
      department: user.department ?? '',
      location: user.location ?? '',
      linkedinUrl: user.linkedinUrl ?? '',
      timezone: user.timezone ?? '',
      notes: user.notes ?? '',
      status: user.status,
      active: user.active,
    },
  });

  const save = async (body: AdminUserUpdate) => {
    await update.mutateAsync({ path: { id: user.id }, body });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getAdminUserQueryKey({ path: { id: user.id } }) }),
      queryClient.invalidateQueries({ queryKey: listAdminUsersQueryKey() }),
    ]);
    notify('Profile saved');
  };

  return (
    <Form form={form} onSubmit={save} guardUnsaved>
      <Card title="Personal information">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextFormField name="fullName" label="Full name" placeholder="Ada Lovelace" />
          <TextFormField name="username" label="Username" placeholder="ada" />
          <TextFormField name="phone" label="Phone" type="tel" placeholder="+1 555 000 0000" />
          <TextFormField name="timezone" label="Time zone" placeholder="America/New_York" />
          <TextAreaFormField name="bio" label="Bio" rows={3} className="sm:col-span-2" />
        </div>
      </Card>

      <Card title="Work">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextFormField name="jobTitle" label="Job title" placeholder="Senior analyst" />
          <TextFormField name="department" label="Department" placeholder="Research" />
          <TextFormField name="location" label="Location" placeholder="New York" />
          <TextFormField
            name="linkedinUrl"
            label="LinkedIn address"
            type="url"
            placeholder="https://linkedin.com/in/…"
          />
        </div>
      </Card>

      <Card title="Account">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectFormField name="status" label="Approval status" options={STATUS_OPTIONS} required />
          <SwitchFormField
            name="active"
            label="Account active"
            description="Switch off to block sign-in without losing the person's history."
          />
          <TextAreaFormField
            name="notes"
            label="Admin notes"
            rows={4}
            description="Only administrators see these."
            className="sm:col-span-2"
          />
        </div>
      </Card>

      <Can policies={['CanManageUsers', 'CanManageOrgMembers']} mode="any">
        <div>
          <SubmitButton>Save changes</SubmitButton>
        </div>
      </Can>
    </Form>
  );
};
